import React from 'react';
import { Card, Row, Col, Statistic, Tabs, Table } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import api from '../services/api';

const StatsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['statsSummary'],
    queryFn: async () => {
      const response = await api.get('/admin/stats');
      return response.data;
    }
  });

  const { data: trendData } = useQuery({
    queryKey: ['statsTrend'],
    queryFn: async () => {
      const response = await api.get('/admin/stats/trend');
      return response.data;
    }
  });

  const { data: valetData } = useQuery({
    queryKey: ['statsValets'],
    queryFn: async () => {
      const response = await api.get('/admin/stats/valets');
      return response.data;
    }
  });

  const valetColumns = [
    { title: t('stats.parkingowy'), dataIndex: 'name', key: 'name' },
    { title: t('stats.completedRequests'), dataIndex: 'completedCount', key: 'completedCount' },
    { title: t('stats.avgDeliveryTime'), dataIndex: 'avgCompletionTime', key: 'avgCompletionTime' },
  ];

  return (
    <div>
      <h1>{t('stats.title')}</h1>
      <Tabs items={[
        {
          key: 'summary',
          label: t('stats.summary'),
          children: (
            <div>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title={t('stats.totalRequests')} value={summaryData?.totalRequests || 0} loading={summaryLoading} />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title={t('stats.completedRequests')} value={summaryData?.completedRequests || 0} loading={summaryLoading} />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title={t('stats.completionRate')} value={summaryData?.completionRate || 0} suffix="%" loading={summaryLoading} />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic title={t('stats.avgPickupTime')} value={summaryData?.avgCompletionTime || 0} suffix="min" loading={summaryLoading} />
                  </Card>
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={12}>
                  <Card title={t('stats.valetAnalysis')}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={trendData?.daily || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" name={t('stats.count')} fill="#1890ff" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title={t('stats.requestTrend')}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trendData?.trend || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="created" name={t('stats.created')} stroke="#1890ff" />
                        <Line type="monotone" dataKey="completed" name={t('stats.completed')} stroke="#52c41a" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
              </Row>
            </div>
          ),
        },
        {
          key: 'valets',
          label: t('stats.valetAnalysis'),
          children: (
            <Card title={t('stats.valetStats')}>
              <Table 
                dataSource={valetData || []} 
                columns={valetColumns} 
                rowKey="id" 
                pagination={false}
              />
            </Card>
          ),
        },
      ]} />
    </div>
  );
};

export default StatsPage;
